import { BlockBlobClient } from "@azure/storage-blob";
import {
  Image,
  mergeClasses,
  Spinner,
  Text,
  tokens,
  Tooltip,
} from "@fluentui/react-components";
import { ArrowDownload20Regular, Delete20Regular } from "@fluentui/react-icons";
import {
  createHelpPageDocument,
  deleteHelpPageDocument,
  getDownloadSignedUrl,
  getHelpPageDocuments,
  getSignedUrl,
} from "api";
import { resolveAsset } from "common/fileIcons";
import { ActionDialog } from "components/actionDialog/ActionDialog";
import { AdminListPage } from "components/adminList/AdminListPage";
import { AdminRowAction } from "components/adminList/AdminListPage.types";
import { useAdminColumnStyles } from "components/adminList/AdminTable.styles";
import { findSidebarItem } from "components/adminSidebar/adminSidebarConfig";
import { useAdminListPagination } from "hooks/useAdminListPagination";
import { useAdminListSearch } from "hooks/useAdminListSearch";
import { useAdminListSort } from "hooks/useAdminListSort";
import { ExportIcon } from "icons/ExportIcon";
import { SearchIcon } from "icons/SearchIcon";
import { ITableColumn } from "models/ITableColumn";
import { useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import {
  useSharedAdminContentHeaderStyles,
  useSharedButtonStyles,
} from "theme/sharedStyles";
import { prepareHeaderBytes } from "utils/blobUtils";
import { formatDate } from "utils/generalUtils";
import { useManageHelpPageDocumentsStyles } from "./ManageHelpPageDocuments.styles";

interface IHelpPageDocument {
  _id: string;
  documentPath: string;
  userId: string;
  display_name: string;
  createdAt: number;
}

export const ManageHelpPageDocuments = (): JSX.Element => {
  const styles = useManageHelpPageDocumentsStyles();
  const colStyles = useAdminColumnStyles();
  const headerClasses = useSharedAdminContentHeaderStyles();
  const buttonStyles = useSharedButtonStyles();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [deletingDocument, setDeletingDocument] =
    useState<IHelpPageDocument | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  const {
    data: documents,
    isLoading,
    refetch,
  } = useQuery<IHelpPageDocument[]>(
    "help-page-documents",
    getHelpPageDocuments,
    {
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
    },
  );

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = [".pdf", ".docx", ".txt", ".md", ".rtf", ".html"];
    const fileExtension = "." + file.name.split(".").pop()?.toLowerCase();

    if (!allowedTypes.includes(fileExtension)) {
      setUploadError(
        `Invalid file type. Allowed types: ${allowedTypes.join(", ")}`,
      );
      return;
    }

    setIsUploading(true);
    setUploadError("");

    try {
      const fileData = await prepareHeaderBytes(file);

      const { sas, containerName, blobName } = await getSignedUrl({
        filename: file.name as any,
        entityType: "helppageknowledge" as any,
        entityId: "" as any,
        fileData,
        conversationId: null,
      });

      if (!sas) {
        throw new Error("Failed to get upload URL");
      }

      const sasUrl = new URL(sas);
      const blobUrl = `${sasUrl.origin}/${containerName}/${blobName}${sasUrl.search}`;
      const blobClient = new BlockBlobClient(blobUrl);
      await blobClient.uploadData(file, {
        blobHTTPHeaders: { blobContentType: file.type },
      });

      await createHelpPageDocument({
        documentPath: blobName,
      });

      await refetch();

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      console.error("Upload error:", error);
      setUploadError("Failed to upload document. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteDocument = (document: IHelpPageDocument) => {
    setDeletingDocument(document);
    setIsDeleteDialogOpen(true);
  };

  const handleDownloadDocument = async (document: IHelpPageDocument) => {
    try {
      const response = await getDownloadSignedUrl({
        filename: document.documentPath,
      });
      if (response?.sas) {
        window.open(response.sas, "_blank");
      } else {
        setUploadError("Failed to download document. Please try again.");
      }
    } catch (error) {
      console.error("Download error:", error);
      setUploadError("Failed to download document. Please try again.");
    }
  };

  const handleConfirmDelete = async () => {
    if (!deletingDocument) return;

    try {
      await deleteHelpPageDocument(deletingDocument._id);
      await refetch();
    } catch (error) {
      console.error("Failed to delete document:", error);
      setUploadError("Failed to delete document. Please try again.");
    } finally {
      setIsDeleteDialogOpen(false);
      setDeletingDocument(null);
    }
  };

  const getFileName = (path: string) => {
    const parts = path.split("/");
    return parts[parts.length - 1] || path;
  };

  const columns: ITableColumn<IHelpPageDocument>[] = useMemo(
    () => [
      {
        columnId: "documentPath",
        displayValue: "Document",
        className: colStyles.wide,
        renderHeaderCell: () => "Document",
        compare: (a: IHelpPageDocument, b: IHelpPageDocument) =>
          getFileName(a.documentPath).localeCompare(
            getFileName(b.documentPath),
          ),
        renderCell: (document: IHelpPageDocument) => (
          <div className={styles.documentInfo}>
            <Image
              src={resolveAsset(document.documentPath)}
              sizes="20x20"
              style={{ width: "20px", height: "20px" }}
            />
            <div>
              <Tooltip
                content={document.documentPath}
                relationship="description"
                positioning="above"
              >
                <Text weight="semibold" className={styles.documentName}>
                  {getFileName(document.documentPath)}
                </Text>
              </Tooltip>
            </div>
          </div>
        ),
      },
      {
        columnId: "display_name",
        displayValue: "Uploaded By",
        className: colStyles.normal,
        renderHeaderCell: () => "Uploaded By",
        compare: (a: IHelpPageDocument, b: IHelpPageDocument) =>
          (a.display_name || "Unknown").localeCompare(
            b.display_name || "Unknown",
          ),
        renderCell: (document: IHelpPageDocument) => (
          <span>{document.display_name || "Unknown"}</span>
        ),
      },
      {
        columnId: "createdAt",
        displayValue: "Upload Date",
        className: colStyles.normal,
        renderHeaderCell: () => "Upload Date",
        compare: (a: IHelpPageDocument, b: IHelpPageDocument) =>
          a.createdAt - b.createdAt,
        renderCell: (document: IHelpPageDocument) => (
          <span>{formatDate(document.createdAt)}</span>
        ),
      },
    ],
    [colStyles, styles],
  );

  const {
    sortColumn,
    isSortDescending,
    sortItems,
    onSortColumnChanged,
    onClearColumnChanged,
  } = useAdminListSort<IHelpPageDocument>({
    cacheKey: "HelpPageDocuments",
    defaultSortColumn: "createdAt",
    defaultDescending: true,
  });

  const searchFields = useMemo(
    () => ["documentPath", "display_name"] as (keyof IHelpPageDocument)[],
    [],
  );
  const {
    searchQuery: internalSearchQuery,
    handleSearchChange,
    handleSearchValueChange,
    filteredItems,
  } = useAdminListSearch<IHelpPageDocument>({
    items: documents || [],
    searchFields,
  });

  const sortedItems = useMemo(
    () => sortItems(filteredItems, columns),
    [filteredItems, sortItems, columns],
  );

  const emptyStateMessage = useMemo(() => {
    return internalSearchQuery
      ? "No documents match your search."
      : 'No documents uploaded. Use "Upload Document" to add documents to the help page knowledge base.';
  }, [internalSearchQuery]);

  const {
    paginatedItems,
    selectedPageIndex,
    pageCount,
    handlePageChange,
    totalItemsCount,
    itemsPerPage,
  } = useAdminListPagination<IHelpPageDocument>({
    items: sortedItems,
    itemsPerPage: 10,
  });

  const rowActions: AdminRowAction<IHelpPageDocument>[] = useMemo(
    () => [
      {
        icon: <ArrowDownload20Regular />,
        tooltip: "Download",
        onClick: handleDownloadDocument,
      },
      {
        icon: <Delete20Regular />,
        tooltip: "Delete",
        onClick: handleDeleteDocument,
      },
    ],
    [],
  );

  const pageTitle =
    findSidebarItem("helpPageDocuments")?.label ?? "Help Page Documents";

  return (
    <>
      <div className={headerClasses.root}>
        <div
          className={mergeClasses(
            headerClasses.titleRow,
            headerClasses.titleRowWithFilters,
          )}
        >
          <h1 className={headerClasses.title}>{pageTitle}</h1>
          <div className={headerClasses.actions}>
            <button
              className={mergeClasses(
                headerClasses.actionButton,
                buttonStyles.secondaryButton,
              )}
              disabled
              type="button"
            >
              <ExportIcon /> Export
            </button>
            <button
              className={mergeClasses(
                headerClasses.actionButton,
                buttonStyles.primaryButton,
              )}
              onClick={handleFileSelect}
              type="button"
            >
              {isUploading ? "Uploading..." : "Upload Document"}
            </button>
          </div>
        </div>
        <div className={headerClasses.filterRow}>
          <div className={headerClasses.searchInputWrapper}>
            <SearchIcon className={headerClasses.searchIcon} />
            <input
              type="text"
              className={headerClasses.searchInput}
              placeholder="Search documents..."
              value={internalSearchQuery}
              onChange={(e) => handleSearchValueChange(e.target.value)}
              aria-label="Search documents..."
            />
          </div>
        </div>
      </div>

      <AdminListPage<IHelpPageDocument>
        title={pageTitle}
        items={documents || []}
        columns={columns}
        isLoading={isLoading || isUploading}
        headerActions={[]}
        searchPlaceholder="Search documents..."
        searchQuery={internalSearchQuery}
        onSearchChange={handleSearchChange}
        hideHeader={true}
        rowActions={rowActions}
        getRowId={(document) => document._id}
        sortColumn={sortColumn}
        isSortDescending={isSortDescending}
        onSortChange={onSortColumnChanged}
        onSortClear={onClearColumnChanged}
        paginatedItems={paginatedItems}
        selectedPageIndex={selectedPageIndex}
        pageCount={pageCount}
        totalItemsCount={totalItemsCount}
        itemsPerPage={itemsPerPage}
        onPageChange={handlePageChange}
        emptyStateMessage={emptyStateMessage}
      />

      {uploadError && (
        <div className={styles.errorMessage}>
          <Text size={200} style={{ color: tokens.colorPaletteRedForeground1 }}>
            {uploadError}
          </Text>
        </div>
      )}

      {isUploading && (
        <div className={styles.uploadingState}>
          <Spinner size="medium" />
          <Text>Uploading document...</Text>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        onChange={handleFileChange}
        style={{ display: "none" }}
        accept=".pdf,.docx,.doc,.txt,.md,.html"
      />

      <ActionDialog
        isVisible={isDeleteDialogOpen}
        title="Delete Document"
        message={
          deletingDocument
            ? `Are you sure you want to delete "${getFileName(deletingDocument.documentPath)}"? This action cannot be undone.`
            : ""
        }
        confirmationMessage="Delete"
        dismissMessage="Cancel"
        isDangerous={true}
        onConfirmation={handleConfirmDelete}
        onDismiss={() => {
          setIsDeleteDialogOpen(false);
          setDeletingDocument(null);
        }}
      />
    </>
  );
};
